import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

import type { AggregateResponse, ResponseRateData } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

const fetchResponseRateData = async (
  endpoint: string,
  signal?: AbortSignal,
): Promise<ResponseRateData> =>
  HttpService.get(endpoint, undefined, undefined, { signal })
    .then(({ data }: AxiosResponse<AggregateResponse>) => {
      if (!Number.isFinite(data.value) || !Number.isFinite(data.totalObjects)) {
        return {};
      }
      if (data.totalObjects === 0) {
        return { responseRate: 0, invited: 0, respondents: data.value };
      }

      return {
        responseRate: (data.value / data.totalObjects) * 100,
        invited: data.totalObjects,
        respondents: data.value,
      };
    })
    .catch((error: AxiosError<string>) => Promise.reject(error.message));

const useResponseRateQuery = (surveyId?: number, enable = false) => {
  const { i18n } = useTranslation();

  const endpoint = useMemo(
    () =>
      generatePath(apiPaths.DASHBOARD.RESPONSE_RATES, {
        surveyId: surveyId ?? '',
      }),
    [surveyId],
  );

  return useQuery<ResponseRateData, string>({
    queryKey: [endpoint, i18n.language],
    queryFn: ({ signal }) => fetchResponseRateData(endpoint, signal),
    enabled: enable && !!surveyId,
  });
};

export default useResponseRateQuery;
